import React from 'react';

/**
 * PIESkeleton — content-shaped loading placeholders for PIE.
 *
 * Replaces the bouncing-dots `pie-loader` so the user sees the page
 * shape arrive before the data does. Each variant matches a real
 * piece of UI (line, heading, card, chart, chip) so the visual jolt
 * when content fills in is minimal.
 *
 * Variants:
 *   - line      — single text line (h: 14px)
 *   - line-lg   — heading line (h: 22px)
 *   - line-xl   — hero heading (h: 36px)
 *   - chip      — small pill (h: 24px, w: 80px)
 *   - circle    — avatar / icon dot
 *   - card      — generic card placeholder (h: 140px)
 *   - chart     — chart-shaped block (h: 200px)
 *
 * Width/height can be overridden via props for fine-tuning.
 */
export default function PIESkeleton({
  variant = 'line',
  width,
  height,
  style,
  className = '',
  ...rest
}) {
  const cls = `pie-sk pie-sk-${variant} ${className}`.trim();
  const merged = { ...style };
  if (width !== undefined) merged.width = typeof width === 'number' ? `${width}px` : width;
  if (height !== undefined) merged.height = typeof height === 'number' ? `${height}px` : height;
  return <span className={cls} style={merged} aria-hidden {...rest} />;
}

/**
 * SkeletonStack — vertically stacks N skeleton lines of varying width
 * so a paragraph placeholder looks organic, not robotic.
 */
export function PIESkeletonParagraph({ lines = 3, lastWidth = '60%', gap = 8 }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap }} aria-hidden>
      {Array.from({ length: lines }, (_, i) => (
        <PIESkeleton
          key={i}
          variant="line"
          width={i === lines - 1 ? lastWidth : '100%'}
        />
      ))}
    </div>
  );
}

/**
 * Page-level skeleton used in place of the old `<Loading />` blocks.
 * Renders a hero (eyebrow + title + sub) and a row of content cards.
 */
export function PIEPageSkeleton({ cards = 3 }) {
  return (
    <div className="pie-page-skeleton" data-testid="pie-page-skeleton">
      <div className="pie-hero-center" style={{ marginBottom: 28 }}>
        <PIESkeleton variant="chip" width={120} style={{ margin: '0 auto 16px' }} />
        <PIESkeleton variant="line-xl" width="68%" style={{ margin: '0 auto 14px' }} />
        <PIESkeleton variant="line" width="48%" style={{ margin: '0 auto' }} />
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${cards}, 1fr)`,
          gap: 16,
        }}
      >
        {Array.from({ length: cards }, (_, i) => (
          <PIESkeleton key={i} variant="card" />
        ))}
      </div>
    </div>
  );
}
